import { useState, useEffect } from "react";
import { Button, Label, TextInput, ToggleSwitch } from "flowbite-react";
import { HiFilter } from "react-icons/hi";
import { useTranslation } from "react-i18next";

interface WorkShiftFiltersProps {
  showInactive: boolean;
  onToggleInactive: (value: boolean) => void;
  onFilterChange: (filters: Record<string, any>) => void;
}

export default function WorkShiftFilters({
  showInactive,
  onToggleInactive,
  onFilterChange,
}: WorkShiftFiltersProps) {
  const { t } = useTranslation(["workShift", "common"]);
  const [showFilters, setShowFilters] = useState(false);

  const [descriptionFilter, setDescriptionFilter] = useState("");
  const [debouncedDescription, setDebouncedDescription] = useState("");

  // Debounce effects
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedDescription(descriptionFilter);
    }, 500);
    return () => clearTimeout(handler);
  }, [descriptionFilter]);

  // Notify parent of filter changes
  useEffect(() => {
    const newFilters: Record<string, any> = {};

    if (debouncedDescription) {
      newFilters["shift_description"] = debouncedDescription;
    }

    onFilterChange(newFilters);
  }, [debouncedDescription]);

  const handleClearFilters = () => {
    setDescriptionFilter("");
    onToggleInactive(false);
  };

  const handleToggleFilters = () => {
    if (showFilters) {
      handleClearFilters();
    }
    setShowFilters(!showFilters);
  };

  return (
    <div>
      <div className="mb-6 flex justify-start">
        <Button color="light" onClick={handleToggleFilters}>
          <HiFilter className="mr-2 h-5 w-5" />
          {showFilters ? t("common:hideFilters") : t("common:showFilters")}
        </Button>
      </div>

      {showFilters && (
        <div className="mb-6 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
            {t("common:filters")}
          </h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
            {/* Shift Description */}
            <div>
              <Label htmlFor="shift-description" className="mb-2 block">
                {t("workShift:description")}
              </Label>
              <TextInput
                id="shift-description"
                className="mt-4"
                placeholder={t("workShift:descriptionPlaceholder") || "Search description"}
                value={descriptionFilter}
                onChange={(e) => setDescriptionFilter(e.target.value)}
              />
            </div>

            {/* Inactive & Reset Actions */}
            <div className="flex flex-col justify-end gap-4 pb-1">
              <ToggleSwitch
                checked={showInactive}
                label={t("common:showInactive")}
                onChange={onToggleInactive}
              />
              <Button color="light" size="sm" onClick={handleClearFilters}>
                {t("common:reset")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
